import React from 'react';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import {
  InfoPopupFooter,
  InfoPopupGlobalFilter,
  InfoPopupHeader,
  InfoPopupSubHeader,
} from '../../components';
import * as BuilderInfoUI from '../../BuilderInfoUI';
import styled from 'styled-components';
import BuilderInfoLabelList from '../../components/BuilderInfoLabelList';
import {
  FunnelModeKey,
  ISegmentCriteriaFieldsFragment,
  ISignificanceQueryFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import isEmpty from 'lodash/isEmpty';
import useAnalysisBuilderInfo from '../../../../redux/selectorHooks/useAnalysisBuilderInfo';
import { UnreachableCaseError } from 'ts-essentials';
import TimeToConvertContent from './TimeToConvertContent';
import ConversionContent from './ConversionContent';
import ThinDivider from '../../../ThinDivider';
import { borderedContent } from '../../../../styles/shareableStyles';

interface IProps {
  analysisId: string;
  onShowInReport?: () => void;
}

const CriteriaContainer = styled.div`
  padding: 4px 8px;
  ${borderedContent};
`;

const CriteriaHeaderRow = styled(BuilderInfoUI.PlainInfo)`
  margin-bottom: 12px;
  column-gap: 8px;
`;

const CriteriaControlRow = styled(BuilderInfoUI.PlainInfo)`
  column-gap: 8px;
  flex-wrap: wrap;
`;

const TextContainer = styled(BuilderInfoUI.InfoLabel)`
  white-space: pre;
  display: flex;
  align-items: center;
`;

const MetricValue = styled(BuilderInfoLabelList)`
  margin-left: 4px;
`;

const CriteriaControlVariant = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  column-gap: 8px;
`;
const SignificanceBuilderContent = ({ analysisId, onShowInReport }: IProps) => {
  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);
  assertCondition(
    analysisBuilderInfo !== null &&
      analysisBuilderInfo?.__typename === 'Significance',
  );
  const {
    query: builderInfo,
    display,
    executedAt,
    description,
    creator,
  } = analysisBuilderInfo;

  const getCohortName = (
    cohort:
      | ISegmentCriteriaFieldsFragment['segmentControlGroup']
      | ISegmentCriteriaFieldsFragment['segmentVariants'][0],
  ) => {
    assertNotNil(cohort.name);
    return cohort.name;
  };
  const criteriaContent = () => {
    if (builderInfo.criteria.__typename === 'BreakdownCriteria') {
      return (
        <>
          <CriteriaHeaderRow>
            <BuilderInfoUI.InfoLabel $noMargin={true}>
              Breakdown
            </BuilderInfoUI.InfoLabel>
            <BuilderInfoLabelList items={[builderInfo.criteria.field.name]} />
          </CriteriaHeaderRow>
          <CriteriaControlRow>
            <BuilderInfoUI.InfoLabel $noMargin={true}>
              Control
            </BuilderInfoUI.InfoLabel>
            <BuilderInfoLabelList items={[builderInfo.criteria.controlGroup]} />

            {builderInfo.criteria.variants.map((variant, index) => (
              <CriteriaControlVariant key={variant + '-' + index}>
                <BuilderInfoUI.InfoLabel $noMargin={true}>
                  Variant {String.fromCharCode('A'.charCodeAt(0) + index)}
                </BuilderInfoUI.InfoLabel>
                <BuilderInfoLabelList items={[variant]} />
              </CriteriaControlVariant>
            ))}
          </CriteriaControlRow>
        </>
      );
    } else if (builderInfo.criteria.__typename === 'ExperimentCriteria') {
      return (
        <>
          <CriteriaHeaderRow>
            <BuilderInfoUI.InfoLabel $noMargin={true}>
              Experiment ID
            </BuilderInfoUI.InfoLabel>
            <BuilderInfoLabelList items={[builderInfo.criteria.experiment]} />
          </CriteriaHeaderRow>
          <CriteriaControlRow>
            <BuilderInfoUI.InfoLabel $noMargin={true}>
              Control
            </BuilderInfoUI.InfoLabel>
            <BuilderInfoLabelList
              items={[builderInfo.criteria.criteriaControlGroup]}
            />

            {builderInfo.criteria.criteriaVariants.map((variant, index) => (
              <CriteriaControlVariant key={variant + '-' + index}>
                <BuilderInfoUI.InfoLabel $noMargin={true}>
                  Variant {String.fromCharCode('A'.charCodeAt(0) + index)}
                </BuilderInfoUI.InfoLabel>
                <BuilderInfoLabelList items={[variant]} />
              </CriteriaControlVariant>
            ))}
          </CriteriaControlRow>
        </>
      );
    } else if (builderInfo.criteria.__typename === 'SegmentCriteria') {
      return (
        <>
          <CriteriaControlRow>
            <BuilderInfoUI.InfoLabel $noMargin={true}>
              Control
            </BuilderInfoUI.InfoLabel>
            <BuilderInfoLabelList
              items={[getCohortName(builderInfo.criteria.segmentControlGroup)]}
            />

            {builderInfo.criteria.segmentVariants.map((variant, index) => (
              <CriteriaControlVariant key={variant.id + '-' + index}>
                <BuilderInfoUI.InfoLabel $noMargin={true}>
                  Variant {String.fromCharCode('A'.charCodeAt(0) + index)}
                </BuilderInfoUI.InfoLabel>
                <BuilderInfoLabelList items={[getCohortName(variant)]} />
              </CriteriaControlVariant>
            ))}
          </CriteriaControlRow>
        </>
      );
    } else {
      throw new UnreachableCaseError(builderInfo.criteria);
    }
  };

  const getMetricName = (
    metric: ISignificanceQueryFieldsFragment['metric'],
  ) => {
    switch (metric.__typename) {
      case 'FunnelMetric':
        return metric.funnel.display;
      case 'MeasureMetric':
        return metric.measure.name;
      case 'RetentionMetric':
        return metric.retention.display;
      case 'QueryMetric':
        return metric.expression.display;
      default:
        throw new UnreachableCaseError(metric);
    }
  };

  return (
    <>
      <InfoPopupHeader
        name={display}
        description={description}
        creator={creator}
        createdAt={executedAt}
      />
      <BuilderInfoUI.MainContent>
        <InfoPopupSubHeader analysisId={analysisId} />
        <BuilderInfoUI.HighlightedLabel>
          Significance Criteria
        </BuilderInfoUI.HighlightedLabel>
        <CriteriaContainer>{criteriaContent()}</CriteriaContainer>

        <TextContainer>
          Perform a
          <BuilderInfoUI.PlainValue>
            {` ${builderInfo.test.name} `}
          </BuilderInfoUI.PlainValue>
          <b>Hypothesis Test</b> with a <b>P-Value</b> of
          <BuilderInfoUI.PlainValue>
            {` ${builderInfo.significanceLevel} `}
          </BuilderInfoUI.PlainValue>
          while maintaining a
          {
            <BuilderInfoUI.PlainValue>
              {` ${builderInfo.confidenceLevel}% `}
            </BuilderInfoUI.PlainValue>
          }
          <b>Confidence Level</b>
        </TextContainer>
        <ThinDivider layout="horizontal" size="full" />
        <BuilderInfoUI.HighlightedLabel>Metric</BuilderInfoUI.HighlightedLabel>
        <TextContainer>
          Metric will be based off
          <BuilderInfoUI.PlainValue>
            {` ${builderInfo.metricType.name} `}
          </BuilderInfoUI.PlainValue>
          <MetricValue items={[getMetricName(builderInfo.metric)]} />
        </TextContainer>
        {builderInfo.metric?.__typename === 'FunnelMetric' &&
          builderInfo.metric.funnel.query.mode?.key ===
            FunnelModeKey.Conversion && (
            <ConversionContent
              convertedOnStep={builderInfo.metric.convertedOnStep}
              steps={builderInfo.metric.funnel.query.steps}
              aliasReplacements={
                builderInfo.metric.funnel.pref.aliasReplacements
              }
            />
          )}

        {builderInfo.metric?.__typename === 'FunnelMetric' &&
          builderInfo.metric.ttcMetric && (
            <TimeToConvertContent
              convertedOnStep={builderInfo.metric.convertedOnStep}
              modeSlices={builderInfo.metric.funnel.query.modeSlices}
              steps={builderInfo.metric.funnel.query.steps}
              aliasReplacements={
                builderInfo.metric.funnel.pref.aliasReplacements
              }
              ttcMetric={builderInfo.metric.ttcMetric}
            />
          )}
        {(!isEmpty(builderInfo.filter) ||
          builderInfo.cohortRefs.length > 0) && (
          <InfoPopupGlobalFilter
            filter={builderInfo?.filter}
            globalCohorts={builderInfo.cohortRefs}
          />
        )}
      </BuilderInfoUI.MainContent>
      <InfoPopupFooter
        analysisId={analysisId}
        onShowInReport={onShowInReport}
      />
    </>
  );
};

export default SignificanceBuilderContent;
